import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";
import { searchGuides, track } from "@/lib/track";

const EXAMPLES = ["prato giallo", "quando potare la magnolia", "insetti sulle foglie", "quanto costa un giardiniere", "eliminare un ceppo"];

export default function ChiediEsperto() {
  const { guides, guideCategories } = useData();
  const [q, setQ] = useState("");
  const results = useMemo(() => (q.length > 1 ? searchGuides(guides, q, guideCategories) : []), [q, guides, guideCategories]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (q.length > 2) track(results.length ? "search" : "search_no_result", q, "/chiedi-all-esperto");
    }, 700);
    return () => clearTimeout(t);
  }, [q, results.length]);

  return (
    <>
      <Seo title="Chiedi all'esperto" description="Descrivi il problema del tuo giardino e consulta le guide più pertinenti preparate da Animaverde." path="/chiedi-all-esperto" />
      <PageHero
        overline="Chiedi all'esperto"
        title="Hai una domanda sul tuo giardino?"
        subtitle="Descrivi il problema e consulta le guide più pertinenti preparate da Animaverde."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Chiedi all'esperto</span></>}
      />
      <section className="bg-av-black py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="relative">
            <Search size={22} className="absolute left-5 top-1/2 -translate-y-1/2 text-av-gray" />
            <input
              data-testid="esperto-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Es. perché il mio prato è giallo?"
              className="w-full rounded-full bg-av-green-dark/40 border border-white/15 pl-14 pr-5 py-4 text-av-white placeholder:text-av-gray/60 focus:border-av-lime outline-none text-lg"
              style={{ backgroundColor: "rgba(22,56,29,0.4)" }}
            />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {EXAMPLES.map((e) => (
              <button key={e} onClick={() => setQ(e)} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-av-gray hover:border-av-lime hover:text-av-lime transition-colors">{e}</button>
            ))}
          </div>

          <div className="mt-8" data-testid="esperto-results">
            {q.length > 1 && (
              results.length > 0 ? (
                <>
                  <p className="text-sm text-av-gray mb-4">I risultati sono informativi. Per una valutazione precisa è consigliato un sopralluogo.</p>
                  <div className="space-y-3">
                    {results.slice(0, 6).map((g) => (
                      <Link key={g.id} to={`/guide/${g.slug}`} className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 p-5 hover:border-av-lime/50 transition-colors">
                        <div>
                          <h3 className="font-display text-lg text-av-white">{g.domanda || g.titolo}</h3>
                          <p className="mt-1 text-sm text-av-gray line-clamp-2">{g.risposta_breve}</p>
                        </div>
                        <ArrowUpRight size={16} className="text-av-lime shrink-0 mt-1" />
                      </Link>
                    ))}
                  </div>
                </>
              ) : (
                <div className="rounded-2xl border border-white/10 p-8 text-center">
                  <p className="text-av-white/85">Non abbiamo ancora pubblicato una guida specifica per questo problema. Puoi inviare una fotografia e una breve descrizione ad Animaverde oppure richiedere un sopralluogo.</p>
                  <div className="mt-6 flex flex-wrap justify-center gap-3">
                    <WhatsAppButton />
                    <Link to="/contatti" className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">Invia una richiesta</Link>
                    <Link to="/diagnostica-giardino" className="inline-flex items-center gap-2 rounded-full border border-white/25 text-av-white px-6 py-3 text-sm font-semibold hover:border-av-lime hover:text-av-lime transition-colors">Diagnostica del giardino</Link>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </section>
    </>
  );
}
